import {
  extractAxiosErrorMessage,
  OutreachGmailDraftResponse,
  useOutreachGmail,
  useOutreachGmailStatus,
} from "@inrange/building-manager-api-client";
import { useState } from "react";
import { Alert, Button, Modal, Spinner } from "react-bootstrap";

// "Put this in the sender's drafts folder", plus the two conversations that can
// interrupt it: the sender has edited the draft by hand, and the email has already
// gone out.
//
// Re-drafting REPLACES the draft in place rather than adding a second one, which is
// what makes the folder a reliable picture of what is waiting to be sent. The flip
// side is that a replace can destroy an edit the sender made, so an edited draft
// stops and asks rather than overwriting quietly.
const OutreachGmailDraftAction: React.FC<{
  leadId: string;
  outreachMessageId: string;
  subject: string;
  body: string;
  toEmail: string | null;
  fontSize: number;
  // Where this exact turn stands relative to the drafts folder, from the backend so
  // the comparison is made in one place. `matchesThisTurn` is what separates "already
  // waiting to go out" from "the folder holds an older wording" after a rerun.
  gmailDraftPlaced: boolean;
  gmailDraftMatchesThisTurn: boolean;
  // Somebody has edited the draft in the folder by hand. Rerunning overwrites it,
  // so this is the difference between "rerunning replaces it" as a fact and as a
  // warning about losing their work.
  gmailDraftEdited: boolean;
  // Whether the three above came from a real read of the folder. False means Gmail
  // was unreachable and they carry our stored view, so the copy stops asserting.
  gmailDraftStateVerified: boolean;
  sentAt: string | null;
  // What the automatic placement did. Only "edited" and "vanished" put a button
  // back: those are the two it declines because a person has to decide.
  autoPlace: string | null;
}> = ({
  leadId,
  outreachMessageId,
  subject,
  body,
  toEmail,
  fontSize,
  gmailDraftPlaced,
  gmailDraftMatchesThisTurn,
  gmailDraftEdited,
  gmailDraftStateVerified,
  sentAt,
  autoPlace,
}) => {
  const statusQuery = useOutreachGmailStatus();
  const { placeGmailDraft } = useOutreachGmail();
  const [error, setError] = useState<string | null>(null);
  const [placed, setPlaced] = useState(false);
  const [edited, setEdited] = useState<OutreachGmailDraftResponse | null>(null);
  const [vanished, setVanished] = useState(false);
  // The backend found this contact already emailed and placed nothing. Its own
  // notice rather than `error`: nothing failed, and the operator's next move is to
  // stop, not retry.
  const [alreadySent, setAlreadySent] = useState<string | null>(null);

  const status = statusQuery.data;

  const place = (confirmProceed: boolean) => {
    setError(null);
    setPlaced(false);
    setAlreadySent(null);
    placeGmailDraft.mutate(
      {
        leadId,
        outreachMessageId,
        body: {
          subject,
          body,
          toEmail: toEmail ?? undefined,
          confirmProceed,
        },
      },
      {
        // Every status is named, so a new one fails loudly here instead of
        // falling through to "placed" and claiming a draft that isn't there.
        onSuccess: (data) => {
          if (data.status === "draft_edited") {
            setEdited(data);
            return;
          }
          setEdited(null);
          switch (data.status) {
            case "draft_vanished":
              setVanished(true);
              return;
            case "not_connected":
              setError(data.message ?? "No sending account is connected.");
              return;
            case "already_sent":
              setAlreadySent(
                data.message ??
                  "This contact has already been emailed, so no new draft was placed."
              );
              return;
            case "placed":
              setPlaced(true);
              return;
            default:
              setError(
                `Unexpected response from the drafts folder: ${String(data.status)}`
              );
          }
        },
        onError: async (err) => setError(await extractAxiosErrorMessage(err)),
      }
    );
  };

  // Never render nothing. This block is the operator's only route to the drafts
  // folder, so a silent absence reads as "the feature isn't here" - which is
  // indistinguishable from a status call that failed, and sends them looking for
  // a button that was never going to appear.
  if (statusQuery.isLoading) {
    return (
      <span style={{ fontSize, color: "#9aa2ad" }}>
        <Spinner animation="border" size="sm" /> Checking the sending account...
      </span>
    );
  }
  if (statusQuery.isError || !status) {
    return (
      <span style={{ fontSize, color: "#b02a37" }}>
        Could not check the sending account, so the drafts-folder action is
        unavailable on this load. Copy the email instead, and reload to retry.
      </span>
    );
  }
  if (!status.configured) {
    const gaps = status.configurationGaps ?? [];
    return (
      <span style={{ fontSize, color: "#9aa2ad" }}>
        Gmail drafting is not configured on this environment - copy the email
        instead.
        {gaps.length > 0 && (
          <ul style={{ margin: "4px 0 0", paddingLeft: 18 }}>
            {gaps.map((gap) => (
              <li key={gap}>{gap}</li>
            ))}
          </ul>
        )}
      </span>
    );
  }
  if (!status.connected) {
    // Deliberately no Connect button here. Connecting from this modal is too late:
    // the drafting has already happened, so the operator has just made a batch of
    // emails that are not where they wanted them. The prompt lives in the leads page
    // header instead, before any of that runs.
    return (
      <span style={{ fontSize, color: "#9aa2ad" }}>
        No sending account is connected, so this cannot go to a drafts folder.
        Connect one at the top of the Leads page, then reopen this.
      </span>
    );
  }

  const mailbox = status.googleEmail ?? "the sender";
  // `placed` covers the moment after a successful push, before anything refetches -
  // otherwise the button would sit there still offering to do what was just done.
  const thisTurnIsInDrafts = placed || gmailDraftMatchesThisTurn;
  // Placement is automatic, so this area reports rather than asks. The button comes
  // back only for the two outcomes the automatic path deliberately declines, because
  // both destroy something a person owns: the sender's hand edits, or the assumption
  // that a draft which left the folder was not already sent.
  // The two outcomes the automatic path declines, because both destroy something a
  // person owns: the sender's hand edits, or the assumption that a draft which left
  // the folder was not already sent. `gmailDraftEdited` is the LIVE read, so an edit
  // made after the run is caught too - the reason this used to say "rerunning
  // replaces it" over a draft somebody had rewritten by hand.
  const needsADecision =
    autoPlace === "edited" || autoPlace === "vanished" || gmailDraftEdited;

  // Sent is terminal: offering to place a draft of an email that has already gone out
  // is how somebody gets contacted twice.
  if (sentAt) {
    return (
      <span style={{ fontSize, color: "#0f5132", fontWeight: 600 }}>
        Sent from {mailbox} on {new Date(sentAt).toLocaleDateString()}.
      </span>
    );
  }

  return (
    <>
      <div
        style={{
          display: "flex",
          alignItems: "center",
          gap: 8,
          flexWrap: "wrap",
        }}
      >
        {thisTurnIsInDrafts && !needsADecision ? (
          <span style={{ fontSize, color: "#0f5132", fontWeight: 600 }}>
            {gmailDraftStateVerified
              ? `Waiting in ${mailbox}'s drafts. Rerunning replaces it.`
              : // Gmail was unreachable, so this is what we last DID, not what is
                // in the folder now. Saying "waiting in drafts" would be asserting
                // something nobody checked.
                `Last placed in ${mailbox}'s drafts. Could not check the folder just now.`}
          </span>
        ) : (
          <>
            {gmailDraftEdited && (
              <span style={{ fontSize, color: "#b26a00" }}>
                The draft in {mailbox}&rsquo;s folder has been edited by hand.
                Rerunning or replacing it will lose those edits.
              </span>
            )}
            {!gmailDraftEdited && autoPlace === "vanished" && (
              <span style={{ fontSize, color: "#b26a00" }}>
                The previous draft is no longer in {mailbox}&rsquo;s folder. It
                was sent, deleted, or replaced by an edit - Gmail can give an
                edited draft a new id, which looks the same to us.
              </span>
            )}
            {autoPlace === "failed" && (
              <span style={{ fontSize, color: "#b02a37" }}>
                Gmail could not be reached, so this is not in the drafts folder
                yet.
              </span>
            )}
            <Button
              size="sm"
              variant="primary"
              disabled={placeGmailDraft.isLoading || !toEmail}
              onClick={() => place(false)}
            >
              {placeGmailDraft.isLoading ? (
                <>
                  <Spinner animation="border" size="sm" /> Placing...
                </>
              ) : gmailDraftPlaced ? (
                // Live: a draft with our id really is in the folder, so this
                // replaces it. When it is gone the next push CREATES one, and
                // offering to "update" a draft that no longer exists is what sent
                // an operator looking for an edit that had actually become a
                // second email.
                `Update the draft in ${mailbox}'s drafts`
              ) : (
                `Put a new draft in ${mailbox}'s drafts`
              )}
            </Button>
          </>
        )}
        {!toEmail && !thisTurnIsInDrafts && (
          <span style={{ fontSize, color: "#9aa2ad" }}>
            No email address for this contact.
          </span>
        )}
      </div>
      {error && (
        <div className="text-danger" style={{ fontSize, marginTop: 4 }}>
          {error}
        </div>
      )}
      {alreadySent && (
        <div style={{ fontSize, marginTop: 4, color: "#b26a00" }}>
          {alreadySent}
        </div>
      )}

      {/* The draft has left the folder. We cannot tell sent from discarded without
          read access to the whole mailbox, so the operator decides rather than us
          guessing and possibly emailing somebody twice. */}
      <Modal show={vanished} onHide={() => setVanished(false)} centered>
        <Modal.Header closeButton>
          <Modal.Title style={{ fontSize: 18 }}>
            That draft is no longer in the drafts folder
          </Modal.Title>
        </Modal.Header>
        <Modal.Body style={{ fontSize: 14 }}>
          It was sent, deleted, or replaced by an edit - Gmail can give an
          edited draft a new id, which looks identical to a deletion from here,
          and we cannot tell the three apart without read access to the whole
          mailbox. If it was sent, placing a new draft risks emailing{" "}
          {toEmail ?? "this contact"} twice.
        </Modal.Body>
        <Modal.Footer>
          <Button
            size="sm"
            variant="secondary"
            onClick={() => setVanished(false)}
          >
            Leave it
          </Button>
          <Button
            size="sm"
            variant="primary"
            disabled={placeGmailDraft.isLoading}
            onClick={() => {
              setVanished(false);
              place(true);
            }}
          >
            Create a new draft anyway
          </Button>
        </Modal.Footer>
      </Modal>

      <Modal show={!!edited} onHide={() => setEdited(null)} size="lg" centered>
        <Modal.Header closeButton>
          <Modal.Title style={{ fontSize: 18 }}>
            That draft has been edited
          </Modal.Title>
        </Modal.Header>
        <Modal.Body>
          <Alert variant="warning" style={{ fontSize: 13 }}>
            The draft in the sending account no longer matches what we wrote, so
            somebody has changed it by hand. Replacing it will lose those edits.
          </Alert>
          <div style={{ fontSize: 12, color: "#8a94a0" }}>
            CURRENTLY IN THE DRAFTS FOLDER
          </div>
          {/* A recipient change counts as an edit too, and can be the only one -
              without these lines the subject and body below would look identical
              to ours and the warning would read as a false alarm. */}
          {(edited?.liveTo || edited?.liveCc || edited?.liveBcc) && (
            <div style={{ fontSize: 13, marginBottom: 4 }}>
              {edited?.liveTo && <div>To: {edited.liveTo}</div>}
              {edited?.liveCc && <div>Cc: {edited.liveCc}</div>}
              {edited?.liveBcc && <div>Bcc: {edited.liveBcc}</div>}
            </div>
          )}
          <div style={{ fontSize: 14, fontWeight: 600, marginBottom: 6 }}>
            {edited?.liveSubject}
          </div>
          <div
            style={{
              whiteSpace: "pre-wrap",
              fontSize: 14,
              background: "#fbfcfd",
              border: "1px solid #dbe2ea",
              borderRadius: 6,
              padding: 10,
              maxHeight: 260,
              overflowY: "auto",
            }}
          >
            {edited?.liveBody}
          </div>
        </Modal.Body>
        <Modal.Footer>
          <Button size="sm" variant="secondary" onClick={() => setEdited(null)}>
            Keep the edited draft
          </Button>
          <Button
            size="sm"
            variant="danger"
            disabled={placeGmailDraft.isLoading}
            onClick={() => {
              setEdited(null);
              place(true);
            }}
          >
            Replace it with this draft
          </Button>
        </Modal.Footer>
      </Modal>
    </>
  );
};

export default OutreachGmailDraftAction;
